import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  listMedia, getMediaConfig, getMediaUrl, updateMedia, deleteMedia, uploadFile,
  formatBytes, UploadError,
  type MediaAsset, type MediaConfig, type MediaVisibility,
} from '../../lib/media';
import { listOrganizations, type Organization } from '../../lib/tenancy';
import { ApiError } from '../../lib/apiClient';
import { usePageChrome } from '../../admin/pageChrome';
import { useAdminScope } from '../../admin/scope';
import './AdminPages.css';

/**
 * The media library.
 *
 * ── PRIVATE IS THE DEFAULT AND IT SAYS SO ────────────────────────────────────────────
 *
 * A private asset shows a PRIVATE badge, has no "copy link" action, and its Open action
 * produces a link that expires — stated on screen, because a link that looks permanent
 * and is not is worse than no link. The storage key is never shown, because it names the
 * bucket layout and the tenant that owns it.
 *
 * ── AN UPLOAD IS NOT DONE WHEN THE TRANSFER FINISHES ─────────────────────────────────
 *
 * The progress bar reaches 100% when the bytes have left the browser. The asset is not
 * listed until the engine has verified the object against what it authorized. A failure
 * at that step is reported as a failure, not hidden behind a full progress bar.
 *
 * ── NO FOLDERS, NO EDITOR ────────────────────────────────────────────────────────────
 *
 * Search and filter, over an organization namespace that already separates tenants. No
 * folder tree, no cropping, no resizing, no transcoding — those are later enhancements
 * and none of them is needed to make uploaded media usable.
 */

function VisibilityBadge({ visibility }: { visibility: MediaVisibility }) {
  return visibility === 'private'
    ? <span className="badge badge--pending" title="Only reachable through a short-lived signed link.">Private</span>
    : <span className="badge badge--ok">Public</span>;
}

function Thumbnail({ asset }: { asset: MediaAsset }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    // Only public images get an inline preview. Fetching a signed URL for every private
    // row would sign links nobody asked for, every time the list renders.
    if (asset.kind !== 'image' || asset.visibility !== 'public') return;
    let cancelled = false;
    getMediaUrl(asset.id).then((r) => { if (!cancelled) setUrl(r.url); }).catch(() => {});
    return () => { cancelled = true; };
  }, [asset.id, asset.kind, asset.visibility]);

  const ratio = asset.width && asset.height ? asset.width / asset.height : 16 / 9;

  if (url) {
    return (
      <div className="med-thumb" style={{ aspectRatio: String(ratio) }}>
        {/* Reserved by the recorded dimensions, so the row does not jump when it loads. */}
        <img src={url} alt="" loading="lazy" />
      </div>
    );
  }
  const glyph = asset.kind === 'video' ? '▶' : asset.kind === 'document' ? '📄' : '🖼';
  return (
    <div className="med-thumb med-thumb--icon" style={{ aspectRatio: String(ratio) }}>
      <span aria-hidden="true">{glyph}</span>
    </div>
  );
}

function AssetCard({ asset, onChanged }: { asset: MediaAsset; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opened, setOpened] = useState<{ url: string; expiresAt: string | null } | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(asset.displayName);

  async function open(download = false) {
    setBusy(true); setError(null);
    try {
      const r = await getMediaUrl(asset.id, download);
      setOpened(r);
      window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open this asset.');
    } finally { setBusy(false); }
  }

  async function copyPublicUrl() {
    const r = await getMediaUrl(asset.id);
    await navigator.clipboard.writeText(r.url);
    setOpened(r);
  }

  async function setVisibility(visibility: MediaVisibility) {
    if (visibility === 'public' && !window.confirm(
      `Make "${asset.displayName}" public? It will be readable by anyone with the link, `
      + 'with no Voleto sign-in.',
    )) return;
    setBusy(true); setError(null);
    try {
      await updateMedia(asset.id, { visibility });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change visibility.');
    } finally { setBusy(false); }
  }

  async function rename() {
    setBusy(true); setError(null);
    try {
      await updateMedia(asset.id, { displayName: name });
      setRenaming(false);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not rename.');
    } finally { setBusy(false); }
  }

  async function remove() {
    if (!window.confirm(
      `Delete "${asset.displayName}"? The file is removed and the record is kept, so the `
      + 'history of this asset stays readable. This cannot be undone.',
    )) return;
    setBusy(true); setError(null);
    try {
      await deleteMedia(asset.id, true);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not delete.');
    } finally { setBusy(false); }
  }

  return (
    <article className="med-card">
      <Thumbnail asset={asset} />

      <div className="med-card__body">
        {renaming ? (
          <div className="med-rename">
            <input className="field__input" value={name} onChange={(e) => setName(e.target.value)}
              aria-label="Display name" />
            <button type="button" className="button button--primary adm-btn-sm"
              disabled={busy || !name.trim()} onClick={() => void rename()}>Save</button>
            <button type="button" className="button button--ghost adm-btn-sm"
              onClick={() => { setRenaming(false); setName(asset.displayName); }}>Cancel</button>
          </div>
        ) : (
          <h3 className="med-card__title" title={asset.originalFilename ?? undefined}>
            {asset.displayName}
          </h3>
        )}

        <div className="net-row__badges">
          <VisibilityBadge visibility={asset.visibility} />
          <span className="badge badge--muted">{asset.mimeType.split('/')[1]}</span>
          <span className="badge badge--muted">{formatBytes(asset.byteSize)}</span>
          {asset.width && asset.height && (
            <span className="badge badge--muted">{asset.width}×{asset.height}</span>
          )}
          {asset.scope === 'platform'
            ? <span className="badge badge--network">Platform</span>
            : <span className="badge badge--muted">{asset.organizationName ?? 'Organization'}</span>}
          {/* Whether the STORE verified the hash, or the browser merely claimed it. */}
          {asset.sha256Verified && (
            <span className="badge badge--ok" title={`SHA-256 ${asset.sha256}`}>Checksum verified</span>
          )}
        </div>

        {error && <p className="alert alert--error" role="alert">{error}</p>}

        {opened && opened.expiresAt && (
          <p className="med-expiry" role="status">
            That link expires at {new Date(opened.expiresAt).toLocaleTimeString()}. Open the
            asset again for a fresh one.
          </p>
        )}

        <div className="med-card__actions">
          <button type="button" className="button button--secondary adm-btn-sm"
            disabled={busy} onClick={() => void open()}>Open</button>

          {/* No permanent-link action for a private object: there is no permanent link. */}
          {asset.visibility === 'public' ? (
            <>
              <button type="button" className="button button--ghost adm-btn-sm"
                onClick={() => void copyPublicUrl()}>Copy URL</button>
              <button type="button" className="button button--ghost adm-btn-sm"
                disabled={busy} onClick={() => void setVisibility('private')}>Make private</button>
            </>
          ) : (
            <button type="button" className="button button--ghost adm-btn-sm"
              disabled={busy} onClick={() => void setVisibility('public')}>Make public</button>
          )}

          <button type="button" className="button button--ghost adm-btn-sm"
            onClick={() => setRenaming(true)}>Rename</button>
          <button type="button" className="button button--ghost adm-btn-sm"
            disabled={busy} onClick={() => void remove()}>Delete</button>
        </div>
      </div>
    </article>
  );
}

function Uploader({
  config, organizationId, orgs, onUploaded,
}: {
  config: MediaConfig | null;
  organizationId: string | null;
  orgs: Organization[];
  onUploaded: () => void;
}) {
  const [target, setTarget] = useState<string>(organizationId ?? '');
  const [visibility, setVisibility] = useState<MediaVisibility>('private');
  const [progress, setProgress] = useState<number | null>(null);
  const [stage, setStage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { if (organizationId) setTarget(organizationId); }, [organizationId]);
  // A local preview URL is a browser object, not storage. Released on unmount.
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  async function handleFile(file: File) {
    setError(null);
    if (!target) { setError('Choose an organization to upload into.'); return; }

    // Convenience only. The server enforces both of these regardless of what is sent.
    const limit = config?.limits[file.type];
    if (!limit) {
      setError(`${file.type || 'That file type'} is not allowed. Allowed: `
        + `${(config?.allowedMimeTypes ?? []).join(', ')}.`);
      return;
    }
    if (file.size > limit.maxBytes) {
      setError(`That file is ${formatBytes(file.size)}; the limit for ${file.type} is `
        + `${formatBytes(limit.maxBytes)}.`);
      return;
    }

    if (file.type.startsWith('image/')) setPreview(URL.createObjectURL(file));
    setProgress(0);
    setStage('Uploading');
    try {
      await uploadFile({
        file,
        organizationId: target,
        visibility,
        onProgress: (fraction) => setProgress(fraction),
      });
      // Only now, after the engine verified the object, is this an asset.
      setStage(null);
      setProgress(null);
      setPreview(null);
      if (inputRef.current) inputRef.current.value = '';
      onUploaded();
    } catch (err) {
      setProgress(null);
      setStage(null);
      setError(err instanceof UploadError
        ? `${err.message} (${err.stage})`
        : 'The upload failed.');
    }
  }

  const transferring = progress !== null;

  return (
    <section className="net-panel med-uploader">
      <h2 className="net-panel__title">Upload</h2>

      {config && !config.configured && (
        <p className="alert alert--error" role="alert">
          Object storage is not configured on this environment, so uploads are unavailable.
        </p>
      )}

      <div className="wiz-grid">
        <label className="field">
          <span className="field__label">Organization</span>
          <select className="field__input" value={target} disabled={transferring}
            onChange={(e) => setTarget(e.target.value)}>
            <option value="">Choose one…</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Visibility</span>
          <select className="field__input" value={visibility} disabled={transferring}
            onChange={(e) => setVisibility(e.target.value as MediaVisibility)}>
            <option value="private">Private — signed links only</option>
            <option value="public">Public — anyone with the link</option>
          </select>
        </label>

        <label className="field">
          <span className="field__label">File</span>
          <input
            ref={inputRef}
            className="field__input"
            type="file"
            disabled={transferring || !config?.configured}
            accept={(config?.allowedMimeTypes ?? []).join(',')}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
        </label>
      </div>

      {preview && (
        <div className="med-preview">
          <img src={preview} alt="" />
          <span>Local preview. Nothing is stored until the upload is verified.</span>
        </div>
      )}

      {transferring && (
        <div className="med-progress">
          <div className="med-progress__bar">
            <div className="med-progress__fill" style={{ width: `${Math.round((progress ?? 0) * 100)}%` }} />
          </div>
          <p role="status">
            {stage} {Math.round((progress ?? 0) * 100)}%
            {progress === 1 ? ' — verifying with the engine…' : ''}
          </p>
        </div>
      )}

      {error && <p className="alert alert--error" role="alert">{error}</p>}

      <p className="med-hint">
        Images, MP4/WebM video and PDF. SVG is not accepted: it can carry scripts, so it is
        not treated as an image.
      </p>
    </section>
  );
}

export function AdminMedia() {
  const { organizationId } = useAdminScope();

  const [assets, setAssets] = useState<MediaAsset[] | null>(null);
  const [config, setConfig] = useState<MediaConfig | null>(null);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');

  usePageChrome({
    title: 'Media',
    subtitle: 'Uploaded images, video and documents. Files live in object storage; this is '
      + 'the record of what they are and who owns them.',
  });

  const load = useCallback(() => {
    listMedia({ organizationId, kind: kind || undefined })
      .then(setAssets)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load the media library.'));
  }, [organizationId, kind]);

  useEffect(load, [load]);

  useEffect(() => {
    getMediaConfig().then(setConfig).catch(() => setConfig(null));
    listOrganizations().then(setOrgs).catch(() => setOrgs([]));
  }, []);

  // Client-side: the library is small, and a round trip per keystroke would be worse.
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return assets;
    return (assets ?? []).filter((a) =>
      a.displayName.toLowerCase().includes(term)
      || (a.originalFilename ?? '').toLowerCase().includes(term)
      || a.mimeType.toLowerCase().includes(term));
  }, [assets, search]);

  return (
    <div className="adm-page">
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <Uploader config={config} organizationId={organizationId} orgs={orgs} onUploaded={load} />

      <div className="adm-filters">
        <input
          className="adm-select cpy-search"
          type="search"
          placeholder="Search media…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search media"
        />
        <select className="adm-select" value={kind} onChange={(e) => setKind(e.target.value)}
          aria-label="Filter by type">
          <option value="">All types</option>
          <option value="image">Images</option>
          <option value="video">Video</option>
          <option value="document">Documents</option>
        </select>
      </div>

      {!assets && !error && <p className="adm-lede">Loading media…</p>}
      {shown && shown.length === 0 && (
        <p className="adm-lede">
          {organizationId
            ? 'No media in this organization yet.'
            : 'No media yet. Uploaded files will appear here once they have been verified.'}
        </p>
      )}

      <div className="med-grid">
        {(shown ?? []).map((asset) => (
          <AssetCard key={asset.id} asset={asset} onChanged={load} />
        ))}
      </div>
    </div>
  );
}
